/**
 * Rejects URL characters that browsers can reinterpret before routing.
 * @param {string} value Raw URL input.
 * @returns {boolean} Whether the value contains a backslash or an ASCII control character.
 */
export function hasUnsafeUrlCharacters(value: string): boolean {
  return [...value].some(
    character => character === '\\' || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
  )
}

/**
 * Resolves a URL only when it stays on the supplied app origin.
 * @param {string} value Relative or absolute URL.
 * @param {string} appOrigin Expected app origin, supplied by the platform.
 * @returns {URL | undefined} Same-origin URL, or undefined for malformed or cross-origin values.
 */
export function resolveSameOriginUrl(value: string, appOrigin: string): URL | undefined {
  if (hasUnsafeUrlCharacters(value)) {
    return undefined
  }

  try {
    const base = new URL(appOrigin)
    const resolved = new URL(value, base.origin)

    return resolved.origin === base.origin ? resolved : undefined
  } catch {
    return undefined
  }
}

/**
 * Checks root-relative routes without accepting protocol-relative or escaped external hosts.
 * @param {string} value Raw URL input.
 * @param {string} appOrigin Expected app origin.
 * @returns {boolean} Whether the value is an app-relative route on the expected origin.
 */
export function isAppRelativeUrl(value: string, appOrigin: string): boolean {
  return value.startsWith('/') && !value.startsWith('//') && resolveSameOriginUrl(value, appOrigin) !== undefined
}
